import { useMutation, useQuery } from "@tanstack/react-query";

import {
  convertObjectToCamelCase,
  detectEnvironment,
  handleApiError,
} from "@shared/helpers";

import { n8nInstance, apiInstance } from "./api";
import { SHARED_QUERIES } from "./queries";

import type { ICompanyPlan } from "@shared/types";

const enviroment = detectEnvironment();

const ENVIROMENTS_URLS = {
  prd: {
    updateCompanyPlans: (planId: string | number, companyId: string | number) =>
      `/v2/companies/${companyId}/plans/${planId}?idCompany=${companyId}`,
    createPlans: (companyId: string | number) =>
      `prd/v1/company-plans?idCompany=${companyId}`,
    getCompanyPlan: (companyId: string | number, planId: string | number) =>
      `/v2/companies/${companyId}/plans/${planId}?idCompany=${companyId}`,
    associatePlans: (companyId: string | number) =>
      `/v2/companies/${companyId}/plans`,
    setActivePlan: (companyId: string | number, planId: number) =>
      `70e5612f-6a88-4fd2-8d03-1086c28ead25/prd/v1/companies/${companyId}/plans/${planId}/active`,
  },
};

const URLS =
  ENVIROMENTS_URLS?.[enviroment as keyof typeof ENVIROMENTS_URLS] ||
  ENVIROMENTS_URLS.prd;

const n8nApi = n8nInstance({
  enableBearerToken: true,
  enableXTokenApi: true,
});

const n8nApiWithoutXToken = n8nInstance({
  enableBearerToken: true,
  enableXTokenApi: false,
});

const api = apiInstance({
  enableBearerToken: true,
  enableXTokenApi: false,
});

export const useGetCompanyPlans = ({
  planId,
  companyId,
}: {
  planId?: string | number;
  companyId?: string;
}) => {
  const getCompanyPlans = async (): Promise<{
    success: boolean;
    message: string;
    data?: ICompanyPlan;
  }> => {
    try {
      const { data } = await api.get(URLS.getCompanyPlan(companyId, planId));

      return {
        success: true,
        message: "Planos da empresa encontrados com sucesso!",
        data: data as ICompanyPlan,
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao buscar os planos da empresa!");
    }
  };

  return useQuery({
    queryKey: [SHARED_QUERIES.COMPANY_PLANS, planId, companyId],
    queryFn: () => getCompanyPlans(),
    enabled: !!planId && !!companyId,
  });
};

export const useCreateCompanyPlan = ({ companyId }: { companyId: string }) => {
  const createCompanyPlan = async ({
    payload,
  }: {
    payload: Partial<ICompanyPlan>;
  }): Promise<{
    success: boolean;
    message: string;
    data?: ICompanyPlan | { errors: any[] };
  }> => {
    try {
      const { data } = await n8nApi.post(URLS.createPlans(companyId), payload);

      return {
        success: true,
        message: "Plano criado com sucesso!",
        data: convertObjectToCamelCase(data) as ICompanyPlan,
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao criar o plano!");
    }
  };

  return useMutation({
    mutationKey: [SHARED_QUERIES.COMPANY_PLANS],
    mutationFn: ({
      payload,
      planId,
    }: {
      planId?: string | number;
      payload: Partial<ICompanyPlan> & { observation?: string };
    }) => createCompanyPlan({ payload }),
  });
};

export const useUpdateCompanyPlan = ({ companyId }: { companyId: string }) => {
  const updateCompanyPlan = async ({
    planId,
    payload,
  }: {
    planId?: string | number;
    payload: Partial<ICompanyPlan>;
  }): Promise<{
    success: boolean;
    message: string;
    data?: ICompanyPlan | { errors: any[] };
  }> => {
    try {
      const { data } = await api.put(
        URLS.updateCompanyPlans(planId, companyId),
        payload,
      );

      return {
        success: true,
        message: "Plano atualizado com sucesso!",
        data: convertObjectToCamelCase(data) as ICompanyPlan,
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao atualizar o plano!");
    }
  };

  return useMutation({
    mutationKey: [SHARED_QUERIES.COMPANY_PLANS],
    mutationFn: (data: {
      planId?: string | number;
      payload: Partial<ICompanyPlan> & { observation?: string };
    }) => updateCompanyPlan(data),
  });
};

export const useAssociateCompanyPlan = ({
  companyId,
}: {
  companyId: string | number;
}) => {
  const associateCompanyPlan = async ({
    plans,
    isDefault = false,
  }: {
    plans: number[];
    isDefault?: boolean;
  }): Promise<{
    success: boolean;
    message: string;
    data?: any;
  }> => {
    try {
      const { data } = await api.patch(URLS.associatePlans(companyId), {
        plans,
        default: isDefault,
      });

      return {
        success: true,
        message: "Plano associado com sucesso!",
        data: convertObjectToCamelCase(data),
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao associar o plano!");
    }
  };

  return useMutation({
    mutationKey: [SHARED_QUERIES.COMPANY_PLANS, companyId],
    mutationFn: associateCompanyPlan,
  });
};

export const useSetActiveCompanyPlan = ({
  companyId,
}: {
  companyId: string | number;
}) => {
  const setActiveCompanyPlan = async ({
    planId,
    type,
  }: {
    planId: number;
    type?: "EARLY" | "TIMELINE";
  }): Promise<{
    success: boolean;
    message: string;
    data?: any;
  }> => {
    try {
      const { data } = await n8nApi.patch(
        `${URLS.setActivePlan(companyId, planId)}`,
        {},
        {
          params: { type },
        },
      );

      return {
        success: true,
        message: "Plano definido como ativo com sucesso!",
        data: convertObjectToCamelCase(data),
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao definir o plano como ativo!");
    }
  };

  return useMutation({
    mutationKey: [SHARED_QUERIES.COMPANY_PLANS, companyId],
    mutationFn: setActiveCompanyPlan,
  });
};
